import { Box, Radio, Stack } from '@mui/material';
import { PropsWithChildren, useMemo, useState } from 'react';
import {
  FieldArrayWithId,
  FieldValues,
  UseFieldArrayReturn,
} from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import {
  AdditionalItemRendererProps,
  FormAwareInputItemDuplicateResolutionProps,
} from '@/components/form/formAwareInputs/FormAwareInput.types';
import { FormAwareInputRepeaterProps } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { XCircleIcon } from '@/components/icons/XCircleIcon';
import { COLORS } from '@/styles/tokens/colors';
import { ArrayFieldNameFromFormShape } from '@/types/react-hook-form';

import { iconSizeByButtonSize } from '../../baseInputs/Button/styles';

export const LABEL_OFFSET_HEIGHT = 24;

export interface InputFieldItemProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
> {
  i: number;
  value: FieldArrayWithId<
    FormShape,
    ArrayFieldNameFromFormShape<FormShape>,
    FieldArrayKeyName
  >;
  render: FormAwareInputRepeaterProps<FormShape, FieldArrayKeyName>['render'];
  fieldArrayProps: Pick<
    UseFieldArrayReturn<
      FormShape,
      ArrayFieldNameFromFormShape<FormShape>,
      FieldArrayKeyName
    >,
    'swap' | 'fields' | 'remove'
  >;
  fieldArrayKeyName: FieldArrayKeyName;
  showDeleteButton: boolean;
  additionalItemRendererProps?: AdditionalItemRendererProps;
  // If true, the delete button will be aligned to the top of the input.
  // If false, the delete button will be centered vertically.
  isSingleLineRepeatedInput?: boolean;
  // An optional callback when the "delete" button is clicked. This is mainly
  // used to track which item was deleted from a DuplicatedInputFieldGroup.
  onDeleteItemClick?: () => void;
}

/**
 * A single input item of a FormAwareInputRepeater.
 */
export function InputFieldItem<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>({
  i,
  value,
  render,
  fieldArrayProps,
  fieldArrayKeyName,
  showDeleteButton,
  additionalItemRendererProps,
  isSingleLineRepeatedInput,
  onDeleteItemClick,
}: InputFieldItemProps<FormShape, FieldArrayKeyName>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  const shouldOffsetForLabel = isSingleLineRepeatedInput && i === 0;

  const [offsetCloseIconForLocator, setOffsetCloseIconForLocator] =
    useState(false);

  const Item = useMemo(() => {
    return render(i, fieldArrayProps, value[fieldArrayKeyName], {
      ...additionalItemRendererProps,
      onRenderHasLocatorButton: setOffsetCloseIconForLocator,
    });
  }, [
    additionalItemRendererProps,
    fieldArrayKeyName,
    fieldArrayProps,
    i,
    render,
    value,
  ]);

  return (
    <Stack
      direction="row"
      alignItems="end"
      key={value[fieldArrayKeyName] ?? i}
      sx={{ mt: 1 }}
      data-testid="repeater-input-item"
    >
      <Box flexGrow={1}>
        {additionalItemRendererProps?.duplicateResolutionConfigs ? (
          <WrappedInputFieldItem
            duplicateResolutionConfigs={
              additionalItemRendererProps.duplicateResolutionConfigs
            }
          >
            {Item}
          </WrappedInputFieldItem>
        ) : (
          Item
        )}
      </Box>
      {showDeleteButton && (
        <Stack
          alignSelf={isSingleLineRepeatedInput ? 'start' : 'center'}
          px={1}
        >
          {offsetCloseIconForLocator && (
            <Box height={`${LABEL_OFFSET_HEIGHT}px`} />
          )}
          {shouldOffsetForLabel && <Box height={`${LABEL_OFFSET_HEIGHT}px`} />}
          <Button
            variant="transparent"
            size="sm"
            onClick={() => {
              fieldArrayProps.remove(i);
              onDeleteItemClick?.();
            }}
            aria-label="Remove"
            disabled={disabledFromContext}
            data-testid="button-remove-repeater-input-item"
            square
          >
            <XCircleIcon size={iconSizeByButtonSize.sm} />
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function WrappedInputFieldItem({
  children,
  duplicateResolutionConfigs,
}: PropsWithChildren<{
  duplicateResolutionConfigs: FormAwareInputItemDuplicateResolutionProps;
}>) {
  const { isSelected, onSelect, onConfirm } = duplicateResolutionConfigs;
  return (
    <Stack direction="row" width="100%" alignItems="end">
      <Radio
        onChange={(_, checked) => {
          if (checked) {
            onSelect();
          }
        }}
        checked={isSelected}
      />
      <Box flexGrow={1}>{children}</Box>
      {isSelected ? (
        <IconButton
          icon={CheckIcon}
          ariaLabel="Confirm duplicate"
          size="sm"
          variant="success-primary"
          onClick={onConfirm}
          sx={{
            p: '3px',
            border: `1px solid ${COLORS.FUNCTIONAL.SUCCESS['600']}`,
            height: '40px',
            width: '40px',
            marginLeft: '12px',
          }}
          data-testid="confirm_duplicate_button"
        />
      ) : (
        <Box marginLeft="12px" height="40px" width="40px" />
      )}
    </Stack>
  );
}
